import { fetchAuthSession } from "aws-amplify/auth";
import CONFIG from "../config";

class WebSocketService {
  constructor() {
    this.ws = null;
    this.sessionId = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 3;
    this.reconnectDelay = 1000;
    this.eventHandlers = new Map();
    this.pingInterval = null;
    this.isConnecting = false;
    this.shouldReconnect = true;
  }

  async getToken() {
    // Check for Midway tokens first
    const method = sessionStorage.getItem("nova_sonic_auth_method");
    if (method === "midway") {
      return sessionStorage.getItem("nova_sonic_auth_id_token") || null;
    }
    // Cognito/Amplify flow: return ID token
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString() || null;
    } catch {
      return null;
    }
  }

  async connect(sessionId = null) {
    if (this.ws?.readyState === WebSocket.OPEN) return;
    if (this.isConnecting) throw new Error("Connection in progress");

    this.isConnecting = true;
    this.shouldReconnect = true;
    this.sessionId = sessionId;
    
    const token = await this.getToken();
    const wsUrl = (CONFIG.wsUrl || CONFIG.apiUrl).replace(/^https?:\/\//, "wss://").replace(/\/$/, "");
    
    // Include token in query params for WebSocket auth
    let url = wsUrl;
    const params = new URLSearchParams();
    if (sessionId) params.append("session_id", sessionId);
    if (token) params.append("token", token);
    if (params.toString()) url += `?${params.toString()}`;

    return new Promise((resolve, reject) => {

      this.ws = new WebSocket(url);
      this.ws.onopen = () => { this.isConnecting = false; this.reconnectAttempts = 0; this.startPing(); this.emit("connection_open"); resolve(); };
      this.ws.onmessage = (e) => { try { this.handleMessage(JSON.parse(e.data)); } catch {} };
      this.ws.onerror = (err) => { this.isConnecting = false; this.emit("connection_error", err); reject(err); };
      this.ws.onclose = () => {
        this.isConnecting = false; this.stopPing(); this.emit("connection_close");
        if (this.shouldReconnect && this.reconnectAttempts < this.maxReconnectAttempts) this.reconnect();
        else if (this.reconnectAttempts >= this.maxReconnectAttempts) this.emit("max_reconnect_attempts_reached");
      };
    });
  }

  disconnect() {
    this.shouldReconnect = false;
    this.stopPing();
    this.ws?.close();
    this.ws = null;
    this.sessionId = null;
    this.reconnectAttempts = 0;
  }

  subscribe(sessionId) {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify({ action: "subscribe", session_id: sessionId }));
  }

  on(eventType, handler) {
    if (!this.eventHandlers.has(eventType)) this.eventHandlers.set(eventType, []);
    this.eventHandlers.get(eventType).push(handler);
  }

  off(eventType, handler) {
    const handlers = this.eventHandlers.get(eventType);
    if (handlers) { const i = handlers.indexOf(handler); if (i !== -1) handlers.splice(i, 1); }
  }

  emit(eventType, data) {
    this.eventHandlers.get(eventType)?.forEach(h => { try { h(data); } catch {} });
  }

  handleMessage(message) {
    if (message.message === "pong" || message.message?.includes("Subscribed")) return;
    if (message.error?.includes("not found") || message.error?.includes("Session")) { this.emit("session_not_found", { error: message.error }); return; }
    if (message.error) { this.emit("error_message", { error: message.error }); return; }
    if (message.event_type === "session_event" && message.event_data) {
      this.emit(message.event_data.event_type, { sessionId: message.session_id, eventData: message.event_data, timestamp: message.timestamp });
      this.emit("session_event", message);
    }
  }

  reconnect() {
    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
    this.emit("reconnecting", { attempt: this.reconnectAttempts, maxAttempts: this.maxReconnectAttempts });
    setTimeout(() => this.connect(this.sessionId).catch(() => {}), delay);
  }

  startPing() { this.pingInterval = setInterval(() => this.ping(), 5 * 60 * 1000); }
  stopPing() { if (this.pingInterval) { clearInterval(this.pingInterval); this.pingInterval = null; } }
  ping() { if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify({ action: "ping" })); }
  isConnected() { return this.ws?.readyState === WebSocket.OPEN; }
}

const websocketService = new WebSocketService();

export default websocketService;
